import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'
import { useLoadMore } from '../../../hooks/useLoadMore'
import { useStructuredDataProduct } from '../../../hooks/useStructuredData'
import useWindowDimensions from '../../../hooks/useWindowDimensions'
import { createFetcher } from '../../../libs/next/api/jsonApiHandler'
import { IfHydrated } from '../../../libs/next/hydratation'
import type { API } from '../../../pages/api/loaders/[handler]'
import { labelScore } from '../../const/ScoreHelper'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { ProductDetailResult } from '../../data/ProductFragment'
import type { ProductReviewResult } from '../../data/ProductReviewFragment'
import type { ScoreResult } from '../../data/ScoreFragment'
import style from '../../styles/ProductPageDetail.module.sass'
import { ScriptData } from '../layout/ScriptData'
import { SimpleSwiper } from '../swiper/SimpleSwiper'
import { Container } from './Container'
import { CustomTooltip } from './CustomTooltip'
import { Hidden } from './Hidden'
import { LoaderListingReview } from './LoaderListingReview'
import { LoaderProductReview } from './LoaderProductReview'
import { ReviewCard } from './ReviewCard'
import { ReviewFormWrapper } from './ReviewFormWrapper'
import { ShowMoreButton } from './ShowMoreButton'

const api = createFetcher<API>('/api/loaders/[handler]')

export function ListProductReview({
	scoreList,
	openLoginModal,
	topPositiveReview,
	topNegativeReview,
	product,
}: {
	product: ProductDetailResult
	scoreList: ScoreResult[]
	openLoginModal: () => void
	topPositiveReview: ProductReviewResult
	topNegativeReview: ProductReviewResult
}) {
	const [orderedBy, setOrderedBy] = useState<'valuability' | 'date'>('valuability')
	const [selected, setSelected] = useState<'all' | 'positiveOnly' | 'negativeOnly'>('all')

	const query = useLoadMore(['productReviews', product.id, orderedBy, selected], null, async (page) =>
		api('productReviews', {
			_req: { query: { page } },
			selected,
			orderedBy,
			productId: product.id,
		}),
	)

	const productReviewsList = query.data?.pages.flatMap((page) => page.items) ?? []

	const scoreListArr = productReviewsList.map((rev) => rev.score?.starsAmount)

	const averageNumber =
		Number(scoreListArr.reduce((previous, current) => Number(previous) + Number(current), 0)) / scoreListArr.length

	const averageScore = scoreList?.find((score) => Number(score?.starsAmount) <= Number(averageNumber))

	const [isNewReviewAdded, setIsNewReviewAdded] = useState(false)

	const setByDate = useCallback(() => {
		setOrderedBy('date')
		setSelected('all')
	}, [])

	const setByValuability = useCallback(() => {
		setOrderedBy('valuability')
		setSelected('all')
	}, [])

	const callback = useCallback((value: boolean) => {
		setIsNewReviewAdded(value)
	}, [])

	useEffect(() => {
		if (isNewReviewAdded) {
			setOrderedBy('date')
			setSelected('all')
		}
	}, [isNewReviewAdded])

	const translate = useTranslate()
	const { width } = useWindowDimensions()
	const isMobile = Number(width) <= 768

	const structuredData = useStructuredDataProduct(product, averageScore?.starsAmount, productReviewsList)

	return (
		<>
			{productReviewsList.length > 0 && (
				<div className={style.reviewSummary}>
					{query.data?.pages[0].totalCount && query.data?.pages[0].totalCount > 0 && (
						<div className={style.reviewAddTitle} key={`key${productReviewsList}`}>
							<Translation tr="productPage.reviews.title" /> ({query.data?.pages[0].totalCount}): &nbsp;
							<span
								className={clsx(
									style.colorRating,
									averageScore?.starsAmount && averageScore?.starsAmount < 3 ? style.redColor : style.greenColor,
								)}
							>
								{averageScore && averageScore.label}
								<div className={style.tooltipWrapper}>
									{averageScore && (
										<>
											<span
												className={clsx(
													style.infoIcon,
													averageScore?.starsAmount && averageScore?.starsAmount < 3 && style.infoIconRed,
												)}
												data-tooltip-id={isMobile ? `tooltipScoreMobile` : 'tooltipScore'}
												data-tip
											>
												i
											</span>
											<CustomTooltip
												tooltipId={isMobile ? `tooltipScoreMobile` : 'tooltipScore'}
												textAfterHover={
													<table className={style.scoreTooltipTable}>
														<tr className={style.scoreTooltipTableHeader}>
															<th className={style.scoreTooltipTableHeaderItem}>
																{translate('productPage.reviews.average.title')}
															</th>
														</tr>
														{scoreList.slice(0, -1).map((scoreItem) => (
															<tr key={scoreItem.id}>
																<td className={style.scoreTooltipTableLabel}>{scoreItem.label}</td>
																<td className={style.scoreTooltipTableExample}>
																	{labelScore(Number(scoreItem.starsAmount))}
																</td>
															</tr>
														))}
													</table>
												}
											/>
										</>
									)}
								</div>
							</span>
						</div>
					)}
					<div className={style.reviewSummaryWrapper}>
						{topPositiveReview && Number(topPositiveReview.stats?.positive) > 0 && (
							<div className={style.reviewTopWrapper}>
								<div className={style.reviewTopPositive}>
									<Translation tr="productPage.reviews.mostValuable" />{' '}
									<span className={style.greenColor}>
										<Translation tr="productPage.reviews.mostValuable.positive" />
									</span>
								</div>
								<div className={style.reviewTopCardWrapper}>
									<ReviewCard review={topPositiveReview} openLoginModal={openLoginModal} />
								</div>
							</div>
						)}
						{topNegativeReview && Number(topNegativeReview.stats?.positive) > 0 && (
							<div className={style.reviewTopWrapper}>
								<div className={style.reviewTopNegative}>
									<Translation tr="productPage.reviews.mostValuable" />{' '}
									<span className={style.redColor}>
										<Translation tr="productPage.reviews.mostValuable.negative" />
									</span>
								</div>
								<div className={style.reviewTopCardWrapper}>
									<ReviewCard review={topNegativeReview} openLoginModal={openLoginModal} />
								</div>
							</div>
						)}
					</div>
				</div>
			)}
			<Container size="narrow" variant="leftStickyAligned" disableGutters>
				<IfHydrated
					yes={() => (
						<ReviewFormWrapper
							scoreOptions={scoreList.filter((item) => item.starsAmount && item.starsAmount > 0)}
							openLoginModal={openLoginModal}
							id={product.id}
							title={
								productReviewsList.length > 0
									? translate('productPage.reviews.form.title.more')
									: translate('productPage.reviews.form.title.first')
							}
							buttonLabel={translate('productPage.reviews.button.add')}
							setOrder={callback}
						/>
					)}
				/>

				{productReviewsList && productReviewsList.length > 0 && (
					<>
						<div className={style.titleReview}>
							<Translation tr="productPage.reviews.sort" />
						</div>
						<div className={style.buttonGroup}>
							<Hidden breakpoint="mobileDown">
								<button
									onClick={setByValuability}
									className={clsx(
										style.button,
										orderedBy === 'valuability' && selected === 'all' ? style.buttonHighlighted : '',
									)}
								>
									<Translation tr="productPage.tab.byValuability" />
								</button>
								<button
									onClick={setByDate}
									className={clsx(
										style.button,
										orderedBy === 'date' && selected === 'all' ? style.buttonHighlighted : '',
									)}
								>
									<Translation tr="productPage.tab.byDate" />
								</button>
								<button
									onClick={() => setSelected('positiveOnly')}
									disabled={!productReviewsList.some((review) => Number(review?.score?.starsAmount) >= 3)}
									className={clsx(style.button, selected === 'positiveOnly' ? style.buttonHighlighted : '')}
								>
									<Translation tr="productPage.tab.positiveOnly" />
								</button>
								<button
									onClick={() => setSelected('negativeOnly')}
									disabled={!productReviewsList.some((review) => Number(review?.score?.starsAmount) < 3)}
									className={clsx(style.button, selected === 'negativeOnly' ? style.buttonHighlighted : '')}
								>
									<Translation tr="productPage.tab.negativeOnly" />
								</button>
							</Hidden>
							<Hidden breakpoint="mobileUp" isGridVisible>
								<SimpleSwiper isTransparent>
									<button
										onClick={setByValuability}
										className={clsx(
											style.button,
											orderedBy === 'valuability' && selected === 'all' ? style.buttonHighlighted : '',
										)}
									>
										<Translation tr="productPage.tab.byValuability" />
									</button>
									<button
										onClick={setByDate}
										className={clsx(
											style.button,
											orderedBy === 'date' && selected === 'all' ? style.buttonHighlighted : '',
										)}
									>
										<Translation tr="productPage.tab.byDate" />
									</button>
									<button
										onClick={() => setSelected('positiveOnly')}
										disabled={!productReviewsList.some((review) => Number(review?.score?.starsAmount) >= 3)}
										className={clsx(style.button, selected === 'positiveOnly' ? style.buttonHighlighted : '')}
									>
										<Translation tr="productPage.tab.positiveOnly" />
									</button>
									<button
										onClick={() => setSelected('negativeOnly')}
										disabled={!productReviewsList.some((review) => Number(review?.score?.starsAmount) < 3)}
										className={clsx(style.button, selected === 'negativeOnly' ? style.buttonHighlighted : '')}
									>
										<Translation tr="productPage.tab.negativeOnly" />
									</button>
								</SimpleSwiper>
							</Hidden>
						</div>

						{query.isFetchedAfterMount ? (
							<>
								<div className={style.productReviewGroup}>
									{productReviewsList.map((review) => (
										<ReviewCard review={review} key={review.id} openLoginModal={openLoginModal} />
									))}
								</div>
								{Boolean(query.hasNextPage) && (
									<div className={style.reviewButtonWrapper}>
										<ShowMoreButton
											handleClick={query.fetchNextPage}
											itemsAmount={Number(query.data?.pages[0]?.totalCount ?? 0) - productReviewsList.length}
										/>
									</div>
								)}
							</>
						) : (
							<LoaderListingReview bannerType={<LoaderProductReview />} />
						)}
					</>
				)}
			</Container>
			{query.isSuccess && <ScriptData data={structuredData} />}
		</>
	)
}
